// ============================================================
// announcements.js
// Data access for the `announcements` collection and for per-user seen state.
//
// COLLECTION SHAPE — flat, ISO string timestamps, matching payments /
// inventoryMovements / shopDrawings rather than inventing a new convention:
//
//   announcements/{autoId}
//     title      string
//     body       string
//     author     display name ('STEVE') — the same identity every other
//                collection uses for a person
//     createdAt  ISO string
//     active     boolean
//
// Removing an announcement sets active:false. Nothing is ever deleted, so the
// history of what the team was told survives.
//
// WHERE SEEN STATE LIVES, and why
// -------------------------------
// One document per user, keyed by display name:
//
//   announcementSeen/{DISPLAYNAME}
//     dismissedIds  [announcementId, …]
//     updatedAt     ISO string
//
// The alternative — a `seenBy` array on the announcement itself — was rejected
// for three reasons:
//   1. It makes every dismissal a write to a document every other reader is
//      also writing to. That is contention on a shared record, and the same
//      read-modify-write hazard the board comments had.
//   2. It grows the announcement without bound as the team grows, and every
//      client that lists announcements downloads everyone else's read receipts.
//   3. Firestore rules cannot express "you may append yourself to this array
//      and nothing else", so it could never be locked down later.
//
// Keying the per-user doc by DISPLAY NAME rather than uid means the id is
// deterministic — a single getDoc, no query, no index — and it matches the
// identity already stored on documents (`salesperson`), board comments
// (`author`) and notifications (`recipient`).
//
// Deliberately NOT localStorage: seen state has to follow the person to the
// other machine in the shop, and has to survive a cleared browser.
// ============================================================

import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, query, where, arrayUnion,
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'announcements';
const SEEN_COLLECTION = 'announcementSeen';

/**
 * Every active announcement, newest first.
 *
 * A single equality filter and NO orderBy, so Firestore's automatic
 * single-field index covers it and nothing has to be deployed. Ordering is done
 * here, the same trade CustomerProfile makes for shopDrawings.
 */
export async function fetchActiveAnnouncements() {
  const snap = await getDocs(query(collection(db, COLLECTION), where('active', '==', true)));
  return sortNewestFirst(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

/** Every announcement including deactivated ones — the owner's screen. */
export async function fetchAllAnnouncements() {
  const snap = await getDocs(collection(db, COLLECTION));
  return sortNewestFirst(snap.docs.map(d => ({ id: d.id, ...d.data() })));
}

/** createdAt is an ISO string, so lexical order is chronological. */
export const sortNewestFirst = (list) =>
  [...list].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));

export async function createAnnouncement({ title, body, author }) {
  const ref = await addDoc(collection(db, COLLECTION), {
    title: String(title || '').trim(),
    body: String(body || '').trim(),
    author: author || '',
    createdAt: new Date().toISOString(),
    active: true,
  });
  return ref.id;
}

/** Publish or retire. Never deletes — the history is the point. */
export async function setAnnouncementActive(id, active) {
  await updateDoc(doc(db, COLLECTION, id), { active: !!active });
}

/** The ids this user has dismissed. Missing doc, or no user, means none. */
export async function fetchDismissedIds(displayName) {
  if (!displayName) return [];
  const snap = await getDoc(doc(db, SEEN_COLLECTION, displayName));
  const ids = snap.exists() ? snap.data()?.dismissedIds : null;
  return Array.isArray(ids) ? ids : [];
}

/**
 * Record that this user has dismissed an announcement.
 *
 * setDoc with merge because the document may not exist yet, and arrayUnion so
 * two tabs dismissing at once cannot clobber one another — the append happens
 * server-side. Dismissing the same id twice is a no-op by construction.
 */
export async function dismissAnnouncement(displayName, announcementId) {
  if (!displayName || !announcementId) return;
  await setDoc(
    doc(db, SEEN_COLLECTION, displayName),
    { dismissedIds: arrayUnion(announcementId), updatedAt: new Date().toISOString() },
    { merge: true },
  );
}

/** Active announcements this user has never dismissed, OLDEST first. */
export function unseenAnnouncements(announcements, dismissedIds) {
  const seen = new Set(dismissedIds || []);
  return (announcements || []).filter(a => a && a.id && !seen.has(a.id)).reverse();
}

/**
 * "5m ago" / "2h ago" / "Yesterday" / "3d ago", falling back to a date beyond a
 * week. Same rule as the notification bell so the two read alike.
 */
export function relativeTime(iso, now = Date.now()) {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const mins = Math.floor((now - then) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
